import domtoimage from "dom-to-image";
import {
  I2x2Option,
  IOutputStyle,
  ViewOrder,
  DisplayOrder,
  OutputType,
  OrderStyle,
} from "@/services/Models";

export const useDownloader = () => {
  // 画像化
  const toImage = async (
    title: string,
    imgs: HTMLElement[],
    style: IOutputStyle,
    size: { width: number; height: number }
  ) => {
    const dataURIs: Map<string, string> = new Map();
    const dateString = getDateString();
    switch (style.outputType) {
      // 縦に連結
      case OutputType.vAlign: {
        const dataURL = await toImageVAlign(imgs, size);
        const filename = getFileName(title, style, dateString);
        dataURIs.set(filename, dataURL);
        break;
      }
      // 分割してダウンロード
      case OutputType.split:
        {
          for (const [index, img] of imgs.entries()) {
            const filename = getFileName(title, style, dateString, index);
            const dataURL = await toImageSplit(img, size);
            dataURIs.set(filename, dataURL);
          }
        }
        break;
      // 選択したものを分割してダウンロード
      case OutputType.splitBySelect:
        {
          for (const index of style.selectFrames) {
            const filename = getFileName(title, style, dateString, index);
            const img = imgs[index];
            const dataURL = await toImageSplit(img, size);
            dataURIs.set(filename, dataURL);
          }
        }
        break;
      // 2x2でダウンロード
      case OutputType.twoBytwo:
        {
          const dataURL = await toImage2x2(imgs, size, style.twoBytwoOption);
          const filename = getFileName(title, style, dateString);
          dataURIs.set(filename, dataURL);
        }
        break;
    }

    return dataURIs;
  };
  // 2×2で結合する場合のdataURL生成
  const toImage2x2 = async (
    imgs: HTMLElement[],
    size: { width: number; height: number },
    options: I2x2Option
  ) => {
    const gridSize = 2;
    const div = document.createElement("div");
    const divStyle = div.style;
    divStyle.width = `${size.width * gridSize}px`;
    divStyle.height = `${size.height * gridSize}px`;
    divStyle.display = "grid";
    divStyle.gridTemplateColumns = "1fr 1fr";
    divStyle.gridTemplateRows = "1fr 1fr";

    // オプションにしたがって並び替え
    const order = options.order == ViewOrder.RTLB ? [2, 0, 3, 1] : [0, 1, 2, 3];
    const orderedImgs = order.map((index) => imgs[index]);
    const layers = orderLayers
      .get(options.order)
      ?.get(options.displayOrder)
      ?.get(options.orderStyle);
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        const index = y * gridSize + x;
        // 画像を配置
        const img = orderedImgs[index];
        const imgStyle = img.style;
        imgStyle.gridColumn = `${x + 1}/${x + 1}`;
        imgStyle.gridRow = `${y + 1}/${y + 1}`;
        imgStyle.width = `${size.width}px`;
        imgStyle.height = `${size.height}px`;
        div.appendChild(img);
        // 読み順レイヤを配置

        const layerURL = layers?.[index];
        if (layerURL === undefined) continue;
        const layer = new Image();
        layer.src = layerURL;
        // レイヤは正方形なので高さに合わせる
        layer.width = size.height;
        layer.height = size.height;
        const layerStyle = layer.style;
        layerStyle.gridColumn = `${x + 1}/${x + 1}`;
        layerStyle.gridRow = `${y + 1}/${y + 1}`;

        let justifySelf = "auto";
        if (options.displayOrder == DisplayOrder.center) {
          justifySelf = x == 0 ? "self-end" : "self-start";
        } else if (options.displayOrder == DisplayOrder.rightTop) {
          justifySelf = "self-end";
        }
        layerStyle.justifySelf = justifySelf;
        div.appendChild(layer);
      }
    }

    return elementToDataURI(div);
  };
  // 4コマを縦に連結する場合のdataURL生成
  const toImageVAlign = async (
    imgs: HTMLElement[],
    size: { width: number; height: number }
  ) => {
    const div = document.createElement("div");
    const divStyle = div.style;
    divStyle.width = `${size.width}px`;
    divStyle.height = `${size.height * 4}px`;
    divStyle.display = "flex";
    divStyle.flexDirection = "column";
    for (const img of imgs) {
      const imgStyle = img.style;
      imgStyle.width = `${size.width}px`;
      imgStyle.height = `${size.height}px`;
      div.appendChild(img);
    }
    return elementToDataURI(div, {
      width: size.width,
      height: size.height * 4,
    });
  };
  // コマを個別に出力する場合のdataURL生成
  const toImageSplit = async (
    img: HTMLElement,
    size: { width: number; height: number }
  ) => {
    const imgStyle = img.style;
    imgStyle.width = `${size.width}px`;
    imgStyle.height = `${size.height}px`;
    return elementToDataURI(img, size);
  };

  const toImageThumbnail = async (imgs: HTMLElement[]) => {
    return toImage2x2(
      imgs,
      // サムネイルのサイズ(212x156)で固定
      {
        width: 212 / 2,
        height: 156 / 2,
      },
      // レイアウト(2x2)で固定
      {
        order: ViewOrder.RTLB,
        displayOrder: DisplayOrder.none,
        orderStyle: OrderStyle.type1,
      }
    );
  };

  // 要素をdataURLに変換
  const elementToDataURI = async (
    elm: HTMLElement,
    size?: { width: number; height: number }
  ) => {
    // 現在の位置設定を退避
    const orgPosition = elm.style.position;
    const orgTop = elm.style.top;
    // 変換中の画像が見切れないように位置変更
    const elmAdjustPosition = "10000px";
    elm.style.position = "absolute";
    elm.style.top = "-" + elmAdjustPosition;
    // 参照位置移動用
    const elmAdjustTransform = "translateY(" + elmAdjustPosition + ")";

    document.body.appendChild(elm);
    const dataURL = await domtoimage.toPng(elm, {
      width: size?.width,
      height: size?.height,
      // 変換対象の位置まで参照位置を平行移動させる
      style: {
        transform: elmAdjustTransform,
      },
    });
    document.body.removeChild(elm);

    // 退避した位置設定で復帰
    elm.style.position = orgPosition;
    elm.style.top = orgTop;
    return dataURL;
  };
  // ダウンロード実行
  const downloadURI = (uri: string, name: string): null => {
    const link = document.createElement("a");
    link.download = `${name}.png`;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return null;
  };

  const replaceFileName = (
    title: string,
    style: IOutputStyle,
    dataURIs: Map<string, string>
  ): Map<string, string> => {
    const repDataURIs: Map<string, string> = new Map();
    const dateString = getDateString();
    const values = dataURIs.values();
    switch (style.outputType) {
      // 縦に連結
      // 2x2でダウンロード
      case OutputType.vAlign:
      case OutputType.twoBytwo:
        {
          const filename = getFileName(title, style, dateString);
          repDataURIs.set(filename, values.next().value);
        }
        break;
      // 分割してダウンロード
      case OutputType.split:
        {
          for (const [index, dataURL] of Array.from(values).entries()) {
            const filename = getFileName(title, style, dateString, index);
            repDataURIs.set(filename, dataURL);
          }
        }
        break;
      // 選択したものを分割してダウンロード
      case OutputType.splitBySelect:
        {
          for (const index of style.selectFrames) {
            const filename = getFileName(title, style, dateString, index);
            repDataURIs.set(filename, values.next().value);
          }
        }
        break;
    }
    return repDataURIs;
  };

  const getDateString = (): string => {
    const now = new Date();
    const dateString = `${now.getFullYear()}${(
      "00" +
      (now.getMonth() + 1)
    ).slice(-2)}${("00" + now.getDate()).slice(-2)}${(
      "00" + now.getHours()
    ).slice(-2)}${("00" + now.getMinutes()).slice(-2)}${(
      "00" + now.getSeconds()
    ).slice(-2)}`;
    return dateString;
  };

  const getFileName = (
    title: string,
    style: IOutputStyle,
    dateString: string,
    index?: number
  ): string => {
    let filename = `${title}_${dateString}`;
    switch (style.outputType) {
      // 縦に連結
      // 2x2でダウンロード
      case OutputType.vAlign:
      case OutputType.twoBytwo:
        break;
      // 分割してダウンロード
      // 選択したものを分割してダウンロード
      case OutputType.split:
      case OutputType.splitBySelect:
        filename = `${filename}_${(index ?? 0) + 1}`;
        break;
    }
    return filename;
  };

  return {
    toImage,
    toImageThumbnail,
    downloadURI,
    replaceFileName,
  };
};

// SVGで生成すれば画像の大きさの割合でスケーリングできそう？
const orderLayers = new Map([
  [
    ViewOrder.LTRB,
    new Map([
      [
        DisplayOrder.center,
        new Map([
          [
            OrderStyle.type1,
            [
              "/orders/ltrb/center/A/1.svg",
              "/orders/ltrb/center/A/2.svg",
              "/orders/ltrb/center/A/3.svg",
              "/orders/ltrb/center/A/4.svg",
            ],
          ],
          [
            OrderStyle.type2,
            [
              "/orders/ltrb/center/B/1.svg",
              "/orders/ltrb/center/B/2.svg",
              "/orders/ltrb/center/B/3.svg",
              "/orders/ltrb/center/B/4.svg",
            ],
          ],
          [
            OrderStyle.type3,
            [
              "/orders/ltrb/center/C/1.svg",
              "/orders/ltrb/center/C/2.svg",
              "/orders/ltrb/center/C/3.svg",
              "/orders/ltrb/center/C/4.svg",
            ],
          ],
        ]),
      ],
      [
        DisplayOrder.rightTop,
        new Map([
          [
            OrderStyle.type1,
            [
              "/orders/ltrb/rightTop/A/1.svg",
              "/orders/ltrb/rightTop/A/2.svg",
              "/orders/ltrb/rightTop/A/3.svg",
              "/orders/ltrb/rightTop/A/4.svg",
            ],
          ],
          [
            OrderStyle.type2,
            [
              "/orders/ltrb/rightTop/B/1.svg",
              "/orders/ltrb/rightTop/B/2.svg",
              "/orders/ltrb/rightTop/B/3.svg",
              "/orders/ltrb/rightTop/B/4.svg",
            ],
          ],
          [
            OrderStyle.type3,
            [
              "/orders/ltrb/rightTop/C/1.svg",
              "/orders/ltrb/rightTop/C/2.svg",
              "/orders/ltrb/rightTop/C/3.svg",
              "/orders/ltrb/rightTop/C/4.svg",
            ],
          ],
        ]),
      ],
    ]),
  ],
  [
    ViewOrder.RTLB,
    new Map([
      [
        DisplayOrder.center,
        new Map([
          [
            OrderStyle.type1,
            [
              "/orders/rtlb/center/A/3.svg",
              "/orders/rtlb/center/A/1.svg",
              "/orders/rtlb/center/A/4.svg",
              "/orders/rtlb/center/A/2.svg",
            ],
          ],
          [
            OrderStyle.type2,
            [
              "/orders/rtlb/center/B/3.svg",
              "/orders/rtlb/center/B/1.svg",
              "/orders/rtlb/center/B/4.svg",
              "/orders/rtlb/center/B/2.svg",
            ],
          ],
          [
            OrderStyle.type3,
            [
              "/orders/rtlb/center/C/3.svg",
              "/orders/rtlb/center/C/1.svg",
              "/orders/rtlb/center/C/4.svg",
              "/orders/rtlb/center/C/2.svg",
            ],
          ],
        ]),
      ],
      [
        DisplayOrder.rightTop,
        new Map([
          [
            OrderStyle.type1,
            [
              "/orders/rtlb/rightTop/A/3.svg",
              "/orders/rtlb/rightTop/A/1.svg",
              "/orders/rtlb/rightTop/A/4.svg",
              "/orders/rtlb/rightTop/A/2.svg",
            ],
          ],
          [
            OrderStyle.type2,
            [
              "/orders/rtlb/rightTop/B/3.svg",
              "/orders/rtlb/rightTop/B/1.svg",
              "/orders/rtlb/rightTop/B/4.svg",
              "/orders/rtlb/rightTop/B/2.svg",
            ],
          ],
          [
            OrderStyle.type3,
            [
              "/orders/rtlb/rightTop/C/3.svg",
              "/orders/rtlb/rightTop/C/1.svg",
              "/orders/rtlb/rightTop/C/4.svg",
              "/orders/rtlb/rightTop/C/2.svg",
            ],
          ],
        ]),
      ],
    ]),
  ],
]);
